/**
 * Forward column propagation for the canvas.
 *
 * Every node's output is JSON rows with known columns, so a node's inputs are
 * simply its upstream neighbours' output columns. We walk the graph in
 * topological order, starting from the columns a *source* reported on Test (and
 * Set constants), and fold each transform's effect on the column set — so a
 * downstream node's inspector shows the columns that will actually arrive there.
 *
 * Returns, per node, the columns on each input port (`in-0`, `in-1`). It's a
 * best-effort, config-time view: a source that hasn't been Tested yet
 * contributes no columns until it is.
 */

import type { Edge, Node } from "@xyflow/react";

type Cols = string[];
type RenamePair = { from?: string; to?: string };

/** Union of keys across an array of row objects (first-seen order). */
function keysOf(rows: unknown[]): Cols {
  const seen = new Set<string>();
  const out: Cols = [];
  for (const row of rows) {
    if (row && typeof row === "object" && !Array.isArray(row)) {
      for (const k of Object.keys(row as Record<string, unknown>)) {
        if (!seen.has(k)) {
          seen.add(k);
          out.push(k);
        }
      }
    }
  }
  return out;
}

function applyRename(cols: Cols, renames: RenamePair[]): Cols {
  const map = new Map<string, string>();
  for (const r of renames) if (r.from && r.to) map.set(r.from, r.to);
  return cols.map((c) => map.get(c) ?? c);
}

function applySelect(cols: Cols, columns: Cols, mode: string): Cols {
  const set = new Set(columns);
  return mode === "drop"
    ? cols.filter((c) => !set.has(c))
    : columns.filter((c) => cols.includes(c));
}

function applyJoin(left: Cols, right: Cols, rightKey: string, suffix: string): Cols {
  const leftSet = new Set(left);
  const rightCols = right
    .filter((c) => c !== rightKey)
    .map((c) => (leftSet.has(c) ? `${c}${suffix}` : c));
  return [...left, ...rightCols];
}

function applyUnion(left: Cols, right: Cols, mode: string): Cols {
  if (mode === "intersection") {
    const r = new Set(right);
    return left.filter((c) => r.has(c));
  }
  const out = [...left];
  for (const c of right) if (!out.includes(c)) out.push(c);
  return out;
}

function outputColumns(
  kind: string,
  config: Record<string, unknown>,
  ports: Record<string, Cols>,
  nodeId: string,
  sourceColumns: Record<string, Cols>,
): Cols {
  const in0 = ports["in-0"] ?? [];
  const in1 = ports["in-1"] ?? [];
  if (
    kind === "blob.source" ||
    kind === "s3.source" ||
    kind === "veeva.query" ||
    kind === "database.source" ||
    kind === "cara.source"
  ) {
    // Prefer columns from a fresh Test this session; else the ones persisted on
    // the node's config by the last Test (so they survive a reload).
    if (sourceColumns[nodeId]) return sourceColumns[nodeId];
    return Array.isArray(config.previewColumns) ? config.previewColumns.map(String) : [];
  }
  if (kind === "data.set") return Array.isArray(config.value) ? keysOf(config.value) : [];
  if (kind === "data.table") return Array.isArray(config.columns) ? config.columns.map(String) : [];
  if (kind === "transform.rename") {
    return applyRename(in0, Array.isArray(config.renames) ? (config.renames as RenamePair[]) : []);
  }
  if (kind === "transform.select") {
    const columns = Array.isArray(config.columns) ? config.columns.map(String) : [];
    return applySelect(in0, columns, String(config.mode ?? "keep"));
  }
  if (kind === "transform.join") {
    return applyJoin(
      in0,
      in1,
      String(config.rightKey ?? config.leftKey ?? ""),
      String(config.suffix ?? "_2"),
    );
  }
  if (kind === "transform.union") {
    return applyUnion(in0, in1, String(config.mode ?? "union"));
  }
  if (kind === "transform.split_column") {
    const into = (Array.isArray(config.into) ? config.into.map(String) : []).filter(Boolean);
    const source = String(config.source ?? "");
    const base = config.keepSource ? in0 : in0.filter((c) => c !== source || into.includes(c));
    return [...new Set([...base, ...into])];
  }
  if (kind === "transform.merge_columns") {
    const cols = Array.isArray(config.columns) ? config.columns.map(String) : [];
    const target = String(config.target ?? "");
    const base = config.keepSources ? in0 : in0.filter((c) => !cols.includes(c) || c === target);
    return target ? [...new Set([...base, target])] : base;
  }
  if (kind === "transform.date_format") {
    const target = String(config.target ?? "");
    return target ? [...new Set([...in0, target])] : in0;
  }
  if (kind === "transform.conditional") {
    const target = String(config.target ?? "");
    return target ? [...new Set([...in0, target])] : in0;
  }
  if (kind === "transform.unpivot") {
    const ids = (Array.isArray(config.idColumns) ? config.idColumns.map(String) : []).filter((c) =>
      in0.includes(c),
    );
    const varName = String(config.varName || "variable");
    const valueName = String(config.valueName || "value");
    return [...new Set([...ids, varName, valueName])];
  }
  if (kind === "ai.predict_metadata") return [...new Set([...in0, ...predictColumns(config)])];
  if (kind === "transform.sql") return sqlOutputColumns(String(config.query ?? ""), in0);
  // convert_type / trim / fill_null / filter / replace / dedupe / explode /
  // display / passthrough — columns unchanged.
  return in0;
}

/** The columns AI · Predict metadata adds: per field the value, its confidence
 *  and evidence, then the per-row status, note, inference id and model. */
export function predictColumns(config: Record<string, unknown>): Cols {
  const fields = Array.isArray(config.fields) ? (config.fields as { name?: unknown }[]) : [];
  const out: Cols = [];
  for (const f of fields) {
    const name = String(f.name ?? "").trim();
    if (name) out.push(name, `${name}__confidence`, `${name}__evidence`);
  }
  return [...out, "_prediction_status", "_prediction_note", "_ai_inference_id", "_model"];
}

/**
 * The columns a `SELECT` produces.
 *
 * SQL can reshape columns arbitrarily, and this used to give up and return
 * nothing — which blanked every picker downstream of a SQL node. A Save to
 * Blob three nodes later could not offer an id column or a folder name,
 * because as far as the canvas knew the table had no columns at all.
 *
 * Reading the SELECT list covers what people actually write. Anything it
 * cannot read falls back to the **input** columns rather than to nothing: a
 * guess that is usually right beats a certainty that is always useless, and a
 * picker keeps a value it does not recognise anyway.
 */
export function sqlOutputColumns(query: string, inputColumns: Cols): Cols {
  const select = /\bselect\b([\s\S]*?)\bfrom\b/i.exec(query);
  if (!select) return inputColumns;
  const list = (select[1] ?? "").trim();
  if (!list || list === "*") return inputColumns;

  const parts = splitTopLevel(list);
  if (parts.length === 0) return inputColumns;

  const out: Cols = [];
  for (const raw of parts) {
    const part = raw.trim();
    if (!part) continue;
    // `*` anywhere in the list means everything upstream, plus whatever else
    // is named — and we cannot order that faithfully, so take the input set.
    if (part === "*" || part.endsWith(".*")) return inputColumns;
    const name = columnNameOf(part);
    if (!name) return inputColumns;
    if (!out.includes(name)) out.push(name);
  }
  return out;
}

/** Split a SELECT list on commas that are not inside quotes or parentheses. */
function splitTopLevel(list: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let quote: string | null = null;
  let start = 0;
  for (let i = 0; i < list.length; i++) {
    const ch = list[i];
    if (quote) {
      if (ch === quote) quote = null;
      continue;
    }
    if (ch === '"' || ch === "'" || ch === "`") quote = ch;
    else if (ch === "(") depth++;
    else if (ch === ")") depth--;
    else if (ch === "," && depth === 0) {
      parts.push(list.slice(start, i));
      start = i + 1;
    }
  }
  parts.push(list.slice(start));
  return parts;
}

/** What one SELECT item is called in the result. */
function columnNameOf(part: string): string | null {
  // An explicit alias wins, and is the only reliable name for an expression.
  const alias = /\bas\s+("([^"]*)"|`([^`]*)`|\[([^\]]*)\]|([A-Za-z_][A-Za-z0-9_]*))\s*$/i.exec(
    part,
  );
  if (alias) return alias[2] ?? alias[3] ?? alias[4] ?? alias[5] ?? null;

  // A bare quoted name — `"id_local-adaptation"` — or a plain one, optionally
  // table-qualified.
  const quoted = /^"([^"]+)"$/.exec(part);
  if (quoted) return quoted[1] ?? null;
  const plain = /^[A-Za-z_][A-Za-z0-9_]*(?:\.([A-Za-z_][A-Za-z0-9_]*))?$/.exec(part);
  if (plain) return plain[1] ?? part;

  // An expression with no alias: SQLite names the column after the expression
  // text itself, which is not something a picker should offer as a column.
  return null;
}

/** Topological order (Kahn); leftover cycle nodes appended so each is visited. */
function topoOrder(nodes: Node[], edges: Edge[], byId: Map<string, Node>): string[] {
  const indeg = new Map<string, number>();
  const adj = new Map<string, string[]>();
  for (const n of nodes) {
    indeg.set(n.id, 0);
    adj.set(n.id, []);
  }
  for (const e of edges) {
    if (byId.has(e.source) && byId.has(e.target)) {
      adj.get(e.source)?.push(e.target);
      indeg.set(e.target, (indeg.get(e.target) ?? 0) + 1);
    }
  }
  const queue = nodes.filter((n) => (indeg.get(n.id) ?? 0) === 0).map((n) => n.id);
  const order: string[] = [];
  while (queue.length > 0) {
    const nid = queue.shift();
    if (nid === undefined) break;
    order.push(nid);
    for (const nxt of adj.get(nid) ?? []) {
      indeg.set(nxt, (indeg.get(nxt) ?? 1) - 1);
      if ((indeg.get(nxt) ?? 0) === 0) queue.push(nxt);
    }
  }
  for (const n of nodes) if (!order.includes(n.id)) order.push(n.id);
  return order;
}

/** Per-node input columns keyed by port: `{ [nodeId]: { "in-0": [...], "in-1": [...] } }`. */
export function computeInputColumns(
  nodes: Node[],
  edges: Edge[],
  sourceColumns: Record<string, Cols>,
): Record<string, Record<string, Cols>> {
  const byId = new Map<string, Node>(nodes.map((n) => [n.id, n]));
  const output: Record<string, Cols> = {};
  const input: Record<string, Record<string, Cols>> = {};

  for (const nid of topoOrder(nodes, edges, byId)) {
    const node = byId.get(nid);
    if (!node) continue;
    const data = node.data as Record<string, unknown>;
    const kind = String(data.kind ?? "");
    const config = (data.config ?? {}) as Record<string, unknown>;

    const ports: Record<string, Cols> = {};
    for (const e of edges) {
      const upstream = output[e.source];
      if (e.target === nid && upstream) {
        ports[e.targetHandle ?? "in-0"] = upstream;
      }
    }
    input[nid] = ports;
    output[nid] = outputColumns(kind, config, ports, nid, sourceColumns);
  }
  return input;
}

/**
 * Walk back from `nodeId` to the nearest Veeva Source and report what it reads —
 * which vault, and which object.
 *
 * The object name is already in the graph — it is the `FROM` clause of the
 * upstream query — so asking the operator to type it again on the target is
 * work the tool should be doing. Returns `null` when nothing upstream is a
 * Veeva Source, which is when the target genuinely has to be told.
 *
 * Walks through transforms: they change columns, not which vault object the
 * rows came from.
 */
export function upstreamVeeva(
  nodes: Node[],
  edges: Edge[],
  nodeId: string,
): {
  kind: "veeva_document" | "veeva_object";
  object: string | null;
  connectionId: string | null;
} | null {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const parents = new Map<string, string[]>();
  for (const e of edges) {
    // Metadata enrichment's rows are its in-0 documents; a supporting table
    // (in-1 …) may come from another vault and is not where they live.
    const target = (byId.get(e.target)?.data ?? {}) as Record<string, unknown>;
    if (target.kind === "ai.predict_metadata" && (e.targetHandle ?? "in-0") !== "in-0") continue;
    parents.set(e.target, [...(parents.get(e.target) ?? []), e.source]);
  }

  const seen = new Set<string>();
  const queue = [...(parents.get(nodeId) ?? [])];
  while (queue.length > 0) {
    const id = queue.shift();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const node = byId.get(id);
    const data = (node?.data ?? {}) as Record<string, unknown>;
    if (String(data.kind ?? "") === "veeva.query") {
      const config = (data.config ?? {}) as Record<string, unknown>;
      const vql = String(config.vql ?? "");
      const connectionId = String(config.connectionId ?? "") || null;
      if (/\bfrom\s+documents\b/i.test(vql)) {
        return { kind: "veeva_document", object: null, connectionId };
      }
      // The builder records the object it is querying, so prefer that over
      // parsing the VQL — the text is the fallback for a hand-typed query.
      const stored = String(config.object ?? "").trim();
      if (stored) return { kind: "veeva_object", object: stored, connectionId };
      const m = /\bfrom\s+([A-Za-z0-9_]+)/i.exec(vql);
      return { kind: "veeva_object", object: m?.[1] ?? null, connectionId };
    }
    queue.push(...(parents.get(id) ?? []));
  }
  return null;
}
